/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useEffect, useId, useState } from "react";
import { useSearchParams } from "react-router";
import {
  BadgeCheck,
  Ban,
  CalendarClock,
  CalendarOff,
  CalendarX2,
  CircleSlash,
  HousePlus,
  Link2Off,
  OctagonMinus,
  Phone,
  RotateCcw,
  Users,
  Wallet,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { ApiResult } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import {
  getDemoCapability,
  runOnboardingScenario,
  type DemoCapability,
  type DemoInvite,
  type DemoOnboardingRun,
  type DemoOnboardingScenario,
  type DemoRecipient,
} from "../api/demo";
import { useChrome } from "../contexts/ChromeContext";
import { useToast } from "../contexts/ToastContext";
import { Facts } from "../components/DetailBits";
import { Button } from "../components/ui/Button";
import { EmptyState } from "../components/ui/EmptyState";
import { Link } from "../components/ui/Link";
import { Select } from "../components/ui/Select";
import { Spinner } from "../components/ui/Spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/Tabs";
import { copyWithToast } from "../lib/clipboard";
import { displayPhone } from "../lib/onboarding";
import { readStorage, writeStorage } from "../lib/storage";
import { DemoPhones } from "./DemoPhones";
import { demoScenarios, type Scenario } from "./demoScenarios";

type ScenarioState = { running: string | null; lastOutcome: string | null };

/** One onboarding card's last run, kept with whose phone it texted. */
type OnboardingRunState = { label: string; phone: string; result: ApiResult<DemoOnboardingRun> };

type OnboardingRuns = Record<string, OnboardingRunState>;

const RECIPIENT_KEY = "demo.recipient";
const RUNS_KEY = "demo.onboardingRuns";

/** The cards survive leaving the page, since the walk sends a presenter to the referral and back. */
function storedRuns(): OnboardingRuns {
  try {
    return JSON.parse(readStorage(RUNS_KEY) ?? "{}") as OnboardingRuns;
  } catch {
    return {};
  }
}

const FAMILY_TAB = "family";
const SCHEDULING_TAB = "scheduling";
const CARD_GRID = "grid gap-4 sm:grid-cols-2 xl:grid-cols-3";

const ONBOARDING_ICONS: Record<string, LucideIcon> = {
  PLAN_ON_FILE: BadgeCheck,
  NO_PLAN: Wallet,
  INACTIVE_PLAN: CircleSlash,
  OUT_OF_NETWORK: Ban,
  CONSULT_ONLY: Phone,
  NO_PROVIDER: CalendarOff,
  RETURNING_FAMILY: HousePlus,
  NO_TIMES_WORK: CalendarX2,
  TIME_TAKEN: CalendarClock,
  TEXT_STOP: OctagonMinus,
  LOST_THE_LINK: Link2Off,
};

/**
 * Staging-only scenario triggers for live demos. Every button drives the real pipeline — real
 * staging-Healthie bookings, the real decision engine, real SMS to the demo phone — so what the
 * audience sees is exactly what production does.
 */
export function Demo() {
  useChrome({ title: "Demo" }, []);
  const { showToast } = useToast();
  const { session } = useAuth();
  const [capability, setCapability] = useState<DemoCapability | null>(null);
  const [checked, setChecked] = useState(false);
  const [toPhone, setToPhone] = useState<string>("");
  const [secondPhone, setSecondPhone] = useState<string>("");
  const [state, setState] = useState<ScenarioState>({ running: null, lastOutcome: null });
  const [onboardingRuns, setOnboardingRuns] = useState<OnboardingRuns>(storedRuns);
  const [searchParams, setSearchParams] = useSearchParams();
  const [managingPhones, setManagingPhones] = useState(false);

  useEffect(() => {
    void getDemoCapability().then((result) => {
      if (result.ok) {
        adoptPeople(result.value, readStorage(RECIPIENT_KEY) ?? "");
      }
      setChecked(true);
    });
  }, []);

  useEffect(() => {
    writeStorage(RUNS_KEY, JSON.stringify(onboardingRuns));
  }, [onboardingRuns]);

  function chooseRecipient(phone: string) {
    setToPhone(phone);
    writeStorage(RECIPIENT_KEY, phone);
  }

  function adoptPeople(next: DemoCapability, preferred: string) {
    setCapability(next);
    const stillThere = next.recipients.some((r) => r.phone === preferred);
    chooseRecipient(stillThere ? preferred : (next.recipients[0]?.phone ?? ""));
    setOnboardingRuns((runs) =>
      Object.fromEntries(
        Object.entries(runs).filter(([, run]) =>
          next.recipients.some((r) => r.phone === run.phone),
        ),
      ),
    );
  }

  /** A selected phone that got a new number stays selected under it. */
  function adoptPhones(next: DemoCapability) {
    const fresh = next.recipients.find(
      (r) => !capability?.recipients.some((old) => old.phone === r.phone),
    );
    const kept = next.recipients.some((r) => r.phone === toPhone);
    adoptPeople(next, kept ? toPhone : (fresh?.phone ?? ""));
  }

  /** A reset ended this phone's runs, so their links no longer work. */
  function dropRunsOf(phone: string) {
    setOnboardingRuns((runs) =>
      Object.fromEntries(Object.entries(runs).filter(([, run]) => run.phone !== phone)),
    );
  }

  async function runScenario(scenario: Pick<Scenario, "id" | "toast" | "run">) {
    setState({ running: scenario.id, lastOutcome: null });
    try {
      const outcome = await scenario.run();
      setState({ running: null, lastOutcome: outcome });
      showToast("success", scenario.toast);
    } catch (e) {
      const message = e instanceof Error ? e.message : "That didn't work";
      setState({ running: null, lastOutcome: message });
      showToast("error", message);
    }
  }

  async function runOnboarding(scenario: DemoOnboardingScenario, recipient: DemoRecipient) {
    setState({ running: scenario.id, lastOutcome: null });
    const result = await runOnboardingScenario(scenario.id, recipient.phone);
    setState({ running: null, lastOutcome: null });
    setOnboardingRuns((runs) => ({
      ...runs,
      [scenario.id]: { label: recipient.label, phone: recipient.phone, result },
    }));
    if (result.ok) {
      showToast("success", `${scenario.title}: referral created`);
    } else {
      showToast("error", result.error.message);
    }
  }

  if (!checked) {
    return (
      <div className="grid h-full place-items-center">
        <Spinner size="lg" />
      </div>
    );
  }
  if (capability === null) {
    return (
      <EmptyState
        title="Demo tools aren't available"
        description="They run on staging only. On staging, this may be a passing error."
        actionLabel="Try again"
        onAction={() => window.location.reload()}
      />
    );
  }

  const others = capability.recipients.filter((r) => r.phone !== toPhone);
  const second = others.some((r) => r.phone === secondPhone)
    ? secondPhone
    : (others[0]?.phone ?? "");
  const selected = capability.recipients.find((r) => r.phone === toPhone);
  const secondControl =
    others.length > 0 ? (
      <Select
        label="Second phone"
        value={second}
        onChange={setSecondPhone}
        disabled={state.running !== null}
        options={others.map((r) => ({ value: r.phone, label: r.label }))}
      />
    ) : selected !== undefined ? (
      <Button variant="ghost" size="sm" onClick={() => setManagingPhones(true)}>
        Add a second phone
      </Button>
    ) : null;
  const signedIn = session.state === "authenticated" ? session.user : null;
  const suggestion = signedIn?.phoneE164
    ? { label: signedIn.firstName ?? "", phone: displayPhone(signedIn.phoneE164) ?? "" }
    : null;
  const { reset, scheduling } = demoScenarios({
    toPhone,
    secondPhone: second,
    bookablePatientHealthieId: capability.bookablePatientHealthieId,
    dropRunsOf,
  });
  const tab = searchParams.get("tab") === SCHEDULING_TAB ? SCHEDULING_TAB : FAMILY_TAB;

  return (
    <div className="space-y-6">
      {selected === undefined ? (
        <EmptyState
          title="No demo phones yet"
          description="Add a phone to run a case."
          actionLabel="Add phone"
          onAction={() => setManagingPhones(true)}
        />
      ) : (
        <div className="flex flex-wrap items-start gap-3">
          <div className="w-full max-w-sm space-y-1.5">
            <Select
              label="Texts go to"
              value={toPhone}
              onChange={chooseRecipient}
              disabled={state.running !== null}
              options={capability.recipients.map((r) => ({
                value: r.phone,
                label: `${r.label} · ${displayPhone(r.phone) ?? r.phone}`,
              }))}
            />
            <p className="text-xs text-text-tertiary">Reset after switching phones.</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="mt-7"
            disabled={state.running !== null}
            onClick={() => setManagingPhones(true)}
          >
            Manage phones
          </Button>
          <Button
            variant="secondary"
            size="sm"
            className="mt-7 ml-auto"
            disabled={state.running !== null}
            onClick={() => void runScenario(reset)}
          >
            {state.running === reset.id ? <Spinner size="xs" /> : <RotateCcw size={14} />}
            Reset
          </Button>
        </div>
      )}
      <DemoPhones
        isOpen={managingPhones}
        onClose={() => setManagingPhones(false)}
        recipients={capability.recipients}
        suggestion={suggestion}
        onChange={adoptPhones}
      />
      {state.lastOutcome !== null && (
        <p className="rounded-2xl border border-border bg-bg-elevated px-4 py-3 text-sm text-text-primary">
          {state.lastOutcome}
        </p>
      )}
      <Tabs
        defaultValue={FAMILY_TAB}
        value={tab}
        onValueChange={(tab) => setSearchParams({ tab }, { replace: true })}
      >
        <TabsList>
          <TabsTrigger value={FAMILY_TAB}>Family onboarding</TabsTrigger>
          <TabsTrigger value={SCHEDULING_TAB}>Scheduling agent</TabsTrigger>
        </TabsList>
        <TabsContent value={FAMILY_TAB} className="space-y-3">
          <p className="text-sm text-text-secondary">
            Each run creates a referral and texts the invite during{" "}
            <Link to="/messaging/sending-hours" className="text-accent underline-offset-2 hover:underline">
              sending hours
            </Link>
            .{capability.caregiverMailbox && ` Family emails go to ${capability.caregiverMailbox}.`}
          </p>
          <div className={CARD_GRID}>
            {capability.onboardingScenarios.map((scenario) => {
              const run = onboardingRuns[scenario.id];
              return (
                <ScenarioCard
                  key={scenario.id}
                  scenario={{
                    id: scenario.id,
                    icon: ONBOARDING_ICONS[scenario.id] ?? Users,
                    title: scenario.title,
                    description: scenario.description,
                    watch: scenario.watch,
                  }}
                  walk={scenario.walk}
                  meta={onboardingMeta(scenario)}
                  running={state.running}
                  disabled={selected === undefined}
                  onRun={
                    scenario.runnable
                      ? () => {
                          if (selected !== undefined) {
                            void runOnboarding(scenario, selected);
                          }
                        }
                      : undefined
                  }
                  result={run ? <RunResult run={run} /> : null}
                />
              );
            })}
          </div>
        </TabsContent>
        <TabsContent value={SCHEDULING_TAB} className="space-y-3">
          <p className="text-sm text-text-secondary">
            Each run plays one waitlist case through the agent.
          </p>
          <div className={CARD_GRID}>
            {scheduling.map((scenario) => (
              <ScenarioCard
                key={scenario.id}
                scenario={scenario}
                running={state.running}
                disabled={
                  selected === undefined || (scenario.needsSecondPhone === true && others.length === 0)
                }
                controls={scenario.needsSecondPhone ? secondControl : null}
                onRun={() => void runScenario(scenario)}
              />
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ScenarioCard({
  scenario,
  controls,
  walk,
  meta,
  running,
  disabled,
  onRun,
  result,
}: {
  scenario: Pick<Scenario, "id" | "icon" | "title" | "description" | "watch">;
  controls?: React.ReactNode;
  walk?: string[];
  meta?: string | null;
  running: string | null;
  disabled: boolean;
  onRun?: (() => void) | undefined;
  result?: React.ReactNode;
}) {
  const Icon = scenario.icon;
  const busy = running === scenario.id;
  const titleId = useId();
  return (
    <section
      aria-labelledby={titleId}
      className="flex flex-col gap-3 rounded-2xl border border-border bg-bg-elevated p-5"
    >
      <div className="flex items-center gap-2.5">
        <Icon size={18} className="text-text-tertiary" />
        <h2 id={titleId} className="text-sm font-semibold text-text-primary">
          {scenario.title}
        </h2>
      </div>
      <p className="text-sm leading-relaxed text-text-secondary">{scenario.description}</p>
      {meta && <p className="text-xs text-text-tertiary">{meta}</p>}
      {walk && (
        <ol className="list-decimal space-y-1 pl-5 text-sm leading-relaxed text-text-secondary">
          {walk.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      )}
      <p className="flex-1 text-xs leading-relaxed text-text-tertiary">Watch: {scenario.watch}</p>
      {controls}
      {onRun && (
        <Button variant="secondary" size="sm" disabled={disabled || running !== null} onClick={onRun}>
          {busy ? (
            <>
              <Spinner size="xs" />
              Running…
            </>
          ) : (
            "Run"
          )}
        </Button>
      )}
      {result}
    </section>
  );
}

const SKIP_REASONS: Record<string, string> = {
  OPTED_OUT_SMS: "this phone texted STOP. Reply START, then reset.",
  NOT_ALLOWLISTED: "this phone isn't a demo phone.",
  UNDELIVERABLE: "an earlier text to this phone couldn't be delivered.",
  FAILED: "the send failed.",
  CANCELLED: "the run was stopped.",
};

function inviteLine(invite: DemoInvite, label: string): string {
  if (invite.sms === "SENT") {
    return `Text sent to ${label}'s phone`;
  }
  if (invite.sms === "UNKNOWN") {
    return "Couldn't confirm the text went out. The link below still works.";
  }
  if (invite.sms === "SKIPPED") {
    const reason = invite.reason ?? "unknown";
    return `Text not sent: ${SKIP_REASONS[reason] ?? reason}`;
  }
  if (invite.scheduledAt === null || new Date(invite.scheduledAt).getTime() <= Date.now()) {
    return `Text to ${label}'s phone is queued, not sent yet`;
  }
  const opens = new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    hour: "numeric",
    minute: "2-digit",
    timeZone: invite.zone ?? undefined,
    timeZoneName: "longGeneric",
  }).format(new Date(invite.scheduledAt));
  return `Text queued for ${opens}`;
}

/** What the case is, at a glance: where the child lives, the plan on the referral, what it books. */
function onboardingMeta(scenario: DemoOnboardingScenario): string | null {
  if (!scenario.runnable) {
    return null;
  }
  const books = scenario.appointmentKind === "CONSULT" ? "Consult" : "Intake";
  return [scenario.stateCode, scenario.insuranceHint ?? "No plan", books].join(" · ");
}

/** The date of birth the way the family types it. */
function typedDob(iso: string): string {
  const [year, month, day] = iso.split("-");
  return `${month}/${day}/${year}`;
}

function RunResult({ run }: { run: OnboardingRunState }) {
  const { showToast } = useToast();
  if (!run.result.ok) {
    return (
      <p role="alert" className="text-sm text-error">
        {run.result.error.message}
      </p>
    );
  }
  const { familyLink, childFirstName, childDob, caregiverEmail, invite, referralId } =
    run.result.value;

  return (
    <div className="space-y-2 text-sm">
      <p className="text-text-primary">{inviteLine(invite, run.label)}</p>
      <Facts
        facts={[
          ["Child", childFirstName],
          ["Date of birth", typedDob(childDob)],
          ["Email", caregiverEmail],
        ]}
      />
      <div className="flex flex-wrap items-center gap-3">
        <a href={familyLink} className="text-accent underline-offset-2 hover:underline">
          Open family link
        </a>
        <Button variant="ghost" size="sm" onClick={() => void copyWithToast(familyLink, showToast, "Family link copied", "Couldn't copy the link")}>
          Copy link
        </Button>
        <Link to={`/referrals/${referralId}`} className="text-accent underline-offset-2 hover:underline">
          Referral page
        </Link>
      </div>
    </div>
  );
}
